/**
 * Schémas d'authentification — source unique de validation.
 *
 * Importés par l'API (qui valide) **et** par le front (qui valide avant
 * d'envoyer). Front et back ne peuvent donc pas diverger : c'est le même
 * objet.
 */
import { z } from 'zod';
import { PERMISSIONS, ROLES } from '../constants/roles.js';
export const schemaConnexion = z.object({
    email: z
        .string()
        .trim()
        .toLowerCase()
        .check(z.email('Adresse professionnelle invalide.'))
        .max(255, 'Adresse trop longue.'),
    // Aucune contrainte de complexité à la CONNEXION : elle n'appartient qu'à
    // la création et au changement de mot de passe. L'imposer ici refuserait
    // les comptes créés avant un durcissement de la règle.
    motDePasse: z.string().min(1, 'Mot de passe requis.').max(200),
});
/**
 * Ce que l'API renvoie après une connexion réussie.
 *
 * Le jeton de rafraîchissement n'y figure pas : il part dans un cookie
 * `httpOnly`, hors de portée du JavaScript de la page (CCT § 5.1).
 */
export const schemaSessionOuverte = z.object({
    jetonAcces: z.string(),
    utilisateur: z.object({
        id: z.uuid(),
        email: z.email(),
        nom: z.string(),
        prenom: z.string(),
        /** Le rôle de BASE (CCT § 6) : second facteur, périmètre, dossier médical. */
        role: z.enum(ROLES),
        /**
         * Le rôle DÉFINI porté par le compte (ADR 0006) — natif ou dérivé — et
         * ses permissions effectives. Le front s'en sert pour MASQUER, jamais
         * pour protéger : le serveur revérifie chaque appel.
         */
        roleCode: z.string(),
        roleLibelle: z.string(),
        permissions: z.array(z.enum(PERMISSIONS)),
        siteId: z.uuid().nullable(),
    }),
});
export const schemaUtilisateurCourant = schemaSessionOuverte.shape.utilisateur.extend({
    derniereConnexion: z.iso.datetime().nullable(),
    /**
     * Nom du site où la personne a déclaré travailler aujourd'hui — le poste
     * de sa session (ADR 0002), et non plus le site de son compte.
     *
     * Sans lui, l'écran n'a qu'un UUID à afficher — et « Site
     * c3a6f6ff-3454-463d… » ne dit rien à personne. Le rendre ici plutôt que
     * de faire charger tout le référentiel par le front évite une requête et
     * une permission de plus sur un écran que tout le monde ouvre.
     */
    siteNom: z.string().nullable(),
    /**
     * Le type de ce même site du poste. AJ-06 : en antenne, le médecin note
     * l'entrée ; en agence, l'accueil (`peutNoterUneEntree`). L'écran s'en sert
     * pour masquer ; le serveur relit le site du poste à chaque entrée.
     */
    siteType: z.enum(['AGENCE', 'ANTENNE']).nullable(),
    /**
     * Le second facteur est-il actif sur ce compte ?
     *
     * Le front en a besoin pour deux choses qu'il ne peut pas deviner : afficher
     * l'état réel dans « Mon compte », et conduire vers l'enrôlement les rôles
     * pour qui le CCT § 5.1 le rend obligatoire. Ce n'est PAS un secret : c'est
     * un état de configuration du compte de l'appelant lui-même.
     */
    totpActive: z.boolean(),
    /**
     * Le nom de l'établissement réglé en Configuration : il est en tête des
     * documents imprimés, que tout rôle de terrain peut imprimer.
     */
    etablissement: z.string(),
    /** Le NIF et le STAT d'AFS réglés en Configuration, en tête du reçu ; `null` tant qu'ils sont vides. */
    nifEtablissement: z.string().nullable(),
    statEtablissement: z.string().nullable(),
});
// ─────────────────────────────────────────────────────────────────────
//  Poste du jour — ADR 0002
// ─────────────────────────────────────────────────────────────────────
/**
 * Un site proposé au moment de prendre son poste.
 *
 * Les indisponibles y figurent aussi, avec leur motif : un agent qui ne
 * trouve pas son antenne dans la liste croit à une panne. Affichée et
 * grisée, elle répond à la question qu'il se pose.
 */
export const schemaOptionPoste = z.object({
    siteId: z.uuid(),
    code: z.string(),
    nom: z.string(),
    type: z.enum(['AGENCE', 'ANTENNE']),
    choisissable: z.boolean(),
    motif: z.enum(['FERME', 'METIER_NON_HEBERGE']).nullable(),
    /** Le site de rattachement du compte : présélectionné, rien de plus. */
    habituel: z.boolean(),
});
export const schemaChoixPoste = z.object({ siteId: z.uuid() });
/**
 * Ce que rend la dernière étape d'un parcours de connexion : la session, ou
 * la déclaration de poste qui la précède.
 *
 * Union discriminée, comme le résultat de connexion : le front traite les
 * deux cas explicitement plutôt que de deviner lequel s'applique.
 */
export const schemaResultatOuverture = z.discriminatedUnion('statut', [
    schemaSessionOuverte.extend({ statut: z.literal('session') }),
    z.object({ statut: z.literal('poste_a_declarer'), jetonTemporaire: z.string() }),
]);
/**
 * Décision 432 — le centre où le Super administrateur travaille, choisi sur
 * la page à tout moment. `null` : aucun centre, le réseau entier. Le serveur
 * le revérifie et le porte sur la session ; l'écran ne fait que le demander.
 */
export const schemaChoixDuCentre = z.strictObject({ siteId: z.uuid().nullable() });
export const schemaCentreChoisi = z.object({
    siteId: z.uuid().nullable(),
    siteNom: z.string().nullable(),
    siteType: z.enum(['AGENCE', 'ANTENNE']).nullable(),
});
/**
 * Ce qu'un motif d'indisponibilité dit à la personne devant l'écran.
 *
 * Pas le code, la phrase : « METIER_NON_HEBERGE » ne dit à personne quoi
 * faire (skill `ux-ecrans-afs`).
 */
export const LIBELLE_MOTIF_POSTE = {
    FERME: 'Fermée',
    METIER_NON_HEBERGE: 'N’héberge pas votre poste',
};
/** Code à six chiffres saisi depuis l'application d'authentification. */
export const schemaCodeTotp = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Le code comporte six chiffres.'),
});
/**
 * Résultat d'une connexion.
 *
 * Union discriminée : le front doit traiter les trois cas explicitement plutôt
 * que de deviner lequel s'applique à partir d'un champ absent.
 *
 * **Deux d'entre eux n'ouvrent aucune session.** Un mot de passe correct ne
 * suffit pas quand le CCT § 5.1 impose un second facteur au rôle : ni quand il
 * est actif et doit être présenté, ni quand il ne l'est pas encore et doit
 * être installé d'abord.
 */
export const schemaResultatConnexion = z.discriminatedUnion('statut', [
    schemaSessionOuverte.extend({ statut: z.literal('session') }),
    z.object({
        statut: z.literal('second_facteur'),
        /** Valable quelques minutes, et uniquement pour présenter un code. */
        jetonTemporaire: z.string(),
    }),
    /**
     * Le mot de passe est bon, mais il est provisoire : celui qui a créé le
     * compte le connaît. Aucune session tant qu'il n'a pas été remplacé.
     */
    z.object({
        statut: z.literal('mot_de_passe_a_changer'),
        jetonTemporaire: z.string(),
    }),
    /**
     * Le rôle impose le second facteur (CCT § 5.1) et le compte ne l'a jamais
     * activé. Le jeton rendu n'ouvre QUE l'enrôlement — c'est ce qui évite le
     * blocage d'amorçage : refuser la session enfermerait dehors le premier
     * administrateur d'une installation neuve, sans aucun moyen de s'enrôler.
     */
    z.object({
        statut: z.literal('enrolement_requis'),
        jetonTemporaire: z.string(),
    }),
    /**
     * L'identité est établie, il reste à déclarer OÙ l'on travaille
     * aujourd'hui (ADR 0002). Ne concerne que les rôles rattachés à un site :
     * un rôle transverse n'a rien à déclarer et reçoit sa session directement.
     */
    z.object({
        statut: z.literal('poste_a_declarer'),
        jetonTemporaire: z.string(),
    }),
]);
/**
 * Ce que renvoie la demande d'activation.
 *
 * `uri` se transforme en QR code côté front ; `secretBase32` est la saisie
 * manuelle de secours, pour un téléphone qui ne peut pas scanner.
 */
export const schemaActivationTotp = z.object({
    uri: z.string().startsWith('otpauth://'),
    secretBase32: z.string(),
});
/**
 * Changement du mot de passe provisoire.
 *
 * La complexité est exigée ICI et à la création d'un compte — jamais à la
 * connexion, où l'imposer refuserait les comptes créés avant un durcissement
 * de la règle.
 *
 * Il n'y a **pas** de rotation périodique : la skill `securite-donnees-sante`
 * l'exclut. C'est un changement unique, celui qui rend le mot de passe connu
 * de son seul titulaire.
 */
export const schemaChangementMotDePasse = z.object({
    motDePasse: z
        .string()
        .min(12, 'Douze caractères au minimum.')
        .max(200, 'Mot de passe trop long.')
        .regex(/[a-z]/, 'Au moins une minuscule.')
        .regex(/[A-Z]/, 'Au moins une majuscule.')
        .regex(/\d/, 'Au moins un chiffre.'),
});
/**
 * Changement volontaire, depuis « Mon compte ».
 *
 * Le mot de passe ACTUEL est exigé, et ce n'est pas une formalité : sans
 * lui, un poste laissé déverrouillé une minute suffirait à changer le mot
 * de passe de son titulaire et à lui prendre son compte. C'est la seule
 * chose qui distingue le titulaire de quiconque passe derrière lui.
 */
export const schemaChangementMotDePasseVolontaire = schemaChangementMotDePasse.extend({
    motDePasseActuel: z.string().min(1, 'Mot de passe actuel requis.').max(200),
});
/**
 * Ce que rend une étape de première connexion : soit l'étape suivante, soit
 * la session. Le front n'a donc jamais à deviner ce qui vient après.
 */
export const schemaEtapeSuivante = z.discriminatedUnion('statut', [
    schemaSessionOuverte.extend({ statut: z.literal('session') }),
    z.object({ statut: z.literal('enrolement_requis'), jetonTemporaire: z.string() }),
    z.object({ statut: z.literal('poste_a_declarer'), jetonTemporaire: z.string() }),
]);
// ─────────────────────────────────────────────────────────────────────
//  Mot de passe oublié
// ─────────────────────────────────────────────────────────────────────
/**
 * Demande d'un lien de réinitialisation.
 *
 * Rien d'autre que l'adresse : demander en plus le nom ou le matricule
 * n'ajouterait aucune sécurité — ce sont des informations qu'un collègue
 * connaît — et bloquerait celui qui les orthographie autrement que la base.
 */
export const schemaDemandeReinitialisation = schemaConnexion.pick({ email: true });
/**
 * Usage du lien reçu.
 *
 * Le mot de passe suit exactement les mêmes règles qu'ailleurs : le schéma
 * est étendu, pas recopié, sinon un durcissement futur ne s'appliquerait
 * qu'à la moitié des chemins.
 */
export const schemaReinitialisationMotDePasse = schemaChangementMotDePasse.extend({
    jeton: z.string().min(1, 'Lien incomplet.').max(200),
});
//# sourceMappingURL=auth.js.map